// MEMORY_REFUSED_STATUS is the `status` a refused memory call reports.
// NOTE: Mirrors MEMORY_REFUSED in `project_memory_tools.py`.
const MEMORY_REFUSED_STATUS = 'refused';

export type MemoryEntry = {
  name: string;
  description: string;
};

export type CallOutcome = {
  output: Record<string, unknown> | null;
  refusal?: string;
  /** settled reports whether a result arrived at all, parseable or not. */
  settled: boolean;
};

/** callOutcome says whether a call is in flight, landed, or was refused. */
export function callOutcome(result: unknown, isError: boolean): CallOutcome {
  const text = typeof result === 'string' ? result : undefined;
  const output = parseObject(text);
  const settled = text !== undefined;

  // NOTE: A tool that raises returns a bare message, not an envelope, so the
  // error flag alone has to count as a refusal.
  if (!isError && output?.status !== MEMORY_REFUSED_STATUS) {
    return {output, settled};
  }

  const refusal =
    asString(output?.message) ?? asString(output?.reason) ?? text ?? '';
  return {output, refusal, settled};
}

function parseObject(text: string | undefined): Record<string, unknown> | null {
  if (!text) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap(item => {
    const text = asString(item);
    return text === undefined ? [] : [text];
  });
}

export function entryList(value: unknown): MemoryEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap(item => {
    const fields = item as Record<string, unknown> | null;
    const name = asString(fields?.name);
    return name === undefined
      ? []
      : [{name, description: asString(fields?.description) ?? ''}];
  });
}
